import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Textarea } from "../../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { toast } from "sonner";
import { Image, Film, Layout, Plus, Pencil, Archive, RefreshCw, Eye, Upload, ArrowLeft, Type, ParkingCircle, QrCode } from "lucide-react";

const API_URL = '';

const CONTENT_TYPES = [
  { value: "image", label: "Image", icon: Image },
  { value: "video", label: "Video", icon: Film },
  { value: "widget", label: "Widget", icon: Layout }
];

const WIDGET_TYPES = [
  { value: "text_announcement", label: "Text / Announcement", icon: Type },
  { value: "parking_message", label: "Parking Message", icon: ParkingCircle },
  { value: "participation_prompt", label: "Participation Prompt (QR)", icon: QrCode }
];

const CONTENT_STATUS = [
  { value: "draft", label: "Draft", color: "bg-slate-500" },
  { value: "active", label: "Active", color: "bg-emerald-500" },
  { value: "archived", label: "Archived", color: "bg-red-500" }
];

const FONT_SIZES = [
  { value: "small", label: "Small" },
  { value: "medium", label: "Medium" },
  { value: "large", label: "Large" },
  { value: "xlarge", label: "Extra Large" }
];

export default function AdminContent() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState([]);
  const [stats, setStats] = useState(null);
  const [filterType, setFilterType] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [activeTab, setActiveTab] = useState("library");
  
  // Dialog states
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [editingContent, setEditingContent] = useState(null);
  const [previewContent, setPreviewContent] = useState(null);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  
  const [formData, setFormData] = useState({
    name: "",
    content_type: "widget",
    widget_type: "text_announcement",
    status: "draft",
    default_duration_seconds: 10,
    widget_config: {
      title: "",
      message: "",
      background_color: "#1e293b",
      text_color: "#ffffff",
      font_size: "large"
    }
  });

  const checkAuth = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/auth/me`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setUser(data);
        return true;
      }
    } catch (err) {
      console.error("Auth check failed:", err);
    }
    navigate("/admin/login");
    return false;
  }, [navigate]);

  const fetchContent = useCallback(async () => {
    try {
      let url = `${API_URL}/api/content?limit=200`;
      if (filterType !== "all") url += `&content_type=${filterType}`;
      if (filterStatus !== "all") url += `&status=${filterStatus}`;
      
      const res = await fetch(url, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setContent(data);
      }
    } catch (err) {
      console.error("Failed to fetch content:", err);
    }
  }, [filterType, filterStatus]);

  const fetchStats = async () => {
    try {
      const res = await fetch(`${API_URL}/api/content/stats/summary`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setStats(data);
      }
    } catch (err) {
      console.error("Failed to fetch stats:", err);
    }
  };

  useEffect(() => {
    const init = async () => {
      const authed = await checkAuth();
      if (authed) {
        setLoading(false);
        await Promise.all([fetchContent(), fetchStats()]);
      }
    };
    init();
  }, [checkAuth, fetchContent]);

  useEffect(() => {
    if (user) fetchContent();
  }, [filterType, filterStatus, user, fetchContent]);

  const handleUpload = async () => {
    if (!uploadFile || !formData.name) {
      toast.error("File and name are required");
      return;
    }
    
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", uploadFile);
      fd.append("name", formData.name);
      fd.append("content_type", formData.content_type);
      fd.append("default_duration_seconds", formData.default_duration_seconds.toString());
      
      const res = await fetch(`${API_URL}/api/content/upload`, {
        method: "POST",
        credentials: "include",
        body: fd
      });
      
      if (res.ok) {
        toast.success("Content uploaded successfully");
        setShowCreateDialog(false);
        resetForm();
        setUploadFile(null);
        fetchContent();
        fetchStats();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to upload");
      }
    } catch (err) {
      toast.error("Failed to upload content");
    } finally {
      setUploading(false);
    }
  };

  const handleCreateWidget = async () => {
    if (!formData.name) {
      toast.error("Name is required");
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/content`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(formData)
      });
      
      if (res.ok) {
        toast.success("Widget created successfully");
        setShowCreateDialog(false);
        resetForm();
        fetchContent();
        fetchStats();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to create widget");
      }
    } catch (err) {
      toast.error("Failed to create widget");
    }
  };

  const handleUpdate = async () => {
    if (!editingContent) return;
    
    try {
      const updateData = {
        name: formData.name,
        status: formData.status,
        default_duration_seconds: formData.default_duration_seconds
      };
      
      if (editingContent.content_type === "widget") {
        updateData.widget_config = formData.widget_config;
      }
      
      const res = await fetch(`${API_URL}/api/content/${editingContent.content_id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(updateData)
      });
      
      if (res.ok) {
        toast.success("Content updated successfully");
        setShowEditDialog(false);
        setEditingContent(null);
        resetForm();
        fetchContent();
      } else {
        toast.error("Failed to update content");
      }
    } catch (err) {
      toast.error("Failed to update content");
    }
  };

  const handleActivate = async (contentId) => {
    try {
      const res = await fetch(`${API_URL}/api/content/${contentId}/activate`, {
        method: "POST",
        credentials: "include"
      });
      
      if (res.ok) {
        toast.success("Content activated");
        fetchContent();
        fetchStats();
      } else {
        toast.error("Failed to activate content");
      }
    } catch (err) {
      toast.error("Failed to activate content");
    }
  };

  const handleArchive = async (contentId) => {
    if (!window.confirm("Are you sure you want to archive this content?")) return;
    
    try {
      const res = await fetch(`${API_URL}/api/content/${contentId}`, {
        method: "DELETE",
        credentials: "include"
      });
      
      if (res.ok) {
        toast.success("Content archived");
        fetchContent();
        fetchStats();
      } else {
        toast.error("Failed to archive content");
      }
    } catch (err) {
      toast.error("Failed to archive content");
    }
  };

  const openEditDialog = (item) => {
    setEditingContent(item);
    setFormData({
      name: item.name,
      content_type: item.content_type,
      widget_type: item.widget_type || "text_announcement",
      status: item.status,
      default_duration_seconds: item.default_duration_seconds || 10,
      widget_config: item.widget_config || {
        title: "",
        message: "",
        background_color: "#1e293b",
        text_color: "#ffffff",
        font_size: "large"
      }
    });
    setShowEditDialog(true);
  };

  const openPreviewDialog = (item) => {
    setPreviewContent(item);
    setShowPreviewDialog(true);
  };

  const resetForm = () => {
    setFormData({
      name: "",
      content_type: "widget",
      widget_type: "text_announcement",
      status: "draft",
      default_duration_seconds: 10,
      widget_config: {
        title: "",
        message: "",
        background_color: "#1e293b",
        text_color: "#ffffff",
        font_size: "large"
      }
    });
  };

  const getContentIcon = (type) => {
    const found = CONTENT_TYPES.find(t => t.value === type);
    return found?.icon || Layout;
  };

  const getStatusBadge = (status) => {
    const found = CONTENT_STATUS.find(s => s.value === status);
    return (
      <Badge className={`${found?.color || "bg-slate-500"} text-white`}>
        {found?.label || status}
      </Badge>
    );
  };

  // Widget Preview Component
  const WidgetPreview = ({ config, widgetType }) => {
    const style = {
      backgroundColor: config?.background_color || "#1e293b",
      color: config?.text_color || "#ffffff",
      padding: "2rem",
      borderRadius: "0.5rem",
      minHeight: "200px",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
      textAlign: "center"
    };
    
    const fontSizeMap = {
      small: "1rem",
      medium: "1.5rem",
      large: "2rem",
      xlarge: "3rem"
    };
    
    const titleSize = fontSizeMap[config?.font_size || "large"];
    
    return (
      <div style={style}>
        {widgetType === "text_announcement" && (
          <>
            {config?.title && <h2 style={{ fontSize: titleSize, marginBottom: "1rem", fontWeight: "bold" }}>{config.title}</h2>}
            {config?.message && <p style={{ fontSize: "1.25rem", opacity: 0.9 }}>{config.message}</p>}
          </>
        )}
        {widgetType === "parking_message" && (
          <>
            <ParkingCircle className="h-16 w-16 mb-4" />
            <h2 style={{ fontSize: titleSize, marginBottom: "1rem", fontWeight: "bold" }}>{config?.title || "Parking Status"}</h2>
            <p style={{ fontSize: "1.25rem" }}>{config?.message || "Designated spaces available"}</p>
          </>
        )}
        {widgetType === "participation_prompt" && (
          <>
            <QrCode className="h-16 w-16 mb-4" />
            <h2 style={{ fontSize: titleSize, marginBottom: "1rem", fontWeight: "bold" }}>{config?.prompt_text || "Scan to Earn Credits!"}</h2>
            <p style={{ fontSize: "1rem", opacity: 0.8 }}>Visit participating retailers</p>
          </>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 text-white">
      {/* Header */}
      <header className="bg-slate-800 border-b border-slate-700 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate("/admin")}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-xl font-bold flex items-center gap-2">
                <Image className="h-6 w-6 text-emerald-400" />
                Content Library
              </h1>
              <p className="text-sm text-slate-400">Manage images, videos, and widgets</p>
            </div>
          </div>
          <Button onClick={() => setShowCreateDialog(true)} className="bg-emerald-600 hover:bg-emerald-700">
            <Plus className="h-4 w-4 mr-2" />
            Add Content
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-6">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <Layout className="h-8 w-8 text-blue-400" />
                  <div>
                    <p className="text-2xl font-bold">{stats.total_content}</p>
                    <p className="text-sm text-slate-400">Total Items</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <Image className="h-8 w-8 text-purple-400" />
                  <div>
                    <p className="text-2xl font-bold">{stats.by_type?.image || 0}</p>
                    <p className="text-sm text-slate-400">Images</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <Film className="h-8 w-8 text-amber-400" />
                  <div>
                    <p className="text-2xl font-bold">{stats.by_type?.video || 0}</p>
                    <p className="text-sm text-slate-400">Videos</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <Layout className="h-8 w-8 text-emerald-400" />
                  <div>
                    <p className="text-2xl font-bold">{stats.by_type?.widget || 0}</p>
                    <p className="text-sm text-slate-400">Widgets</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap gap-4 mb-6">
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-48 bg-slate-800 border-slate-700">
              <SelectValue placeholder="Filter by type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {CONTENT_TYPES.map(type => (
                <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-48 bg-slate-800 border-slate-700">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {CONTENT_STATUS.map(status => (
                <SelectItem key={status.value} value={status.value}>{status.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Button variant="outline" onClick={() => { fetchContent(); fetchStats(); }}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Content Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {content.map(item => {
            const ContentIcon = getContentIcon(item.content_type);
            return (
              <Card key={item.content_id} className={`bg-slate-800 border-slate-700 ${item.status === 'archived' ? 'opacity-60' : ''}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      <ContentIcon className="h-5 w-5 text-slate-400" />
                      <CardTitle className="text-lg">{item.name}</CardTitle>
                    </div>
                    {getStatusBadge(item.status)}
                  </div>
                  <CardDescription className="capitalize">
                    {item.content_type}{item.widget_type ? ` - ${item.widget_type.replace(/_/g, ' ')}` : ''}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {/* Preview thumbnail */}
                  <div className="mb-4 rounded overflow-hidden bg-slate-700 aspect-video flex items-center justify-center">
                    {item.content_type === "image" && item.media_url && (
                      <img src={`${API_URL}${item.media_url}`} alt={item.name} className="w-full h-full object-cover" />
                    )}
                    {item.content_type === "video" && (
                      <Film className="h-12 w-12 text-slate-500" />
                    )}
                    {item.content_type === "widget" && (
                      <div className="w-full h-full p-2">
                        <WidgetPreview config={item.widget_config} widgetType={item.widget_type} />
                      </div>
                    )}
                  </div>
                  
                  <div className="text-sm text-slate-400 mb-4">
                    Duration: {item.default_duration_seconds}s
                    {item.media_size_bytes && (
                      <span className="ml-2">• {(item.media_size_bytes / 1024 / 1024).toFixed(2)} MB</span>
                    )}
                  </div>
                  
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => openPreviewDialog(item)}>
                      <Eye className="h-3 w-3 mr-1" />
                      Preview
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => openEditDialog(item)}>
                      <Pencil className="h-3 w-3 mr-1" />
                      Edit
                    </Button>
                    {item.status === "draft" && (
                      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => handleActivate(item.content_id)}>
                        Activate
                      </Button>
                    )}
                    {item.status !== "archived" && (
                      <Button size="sm" variant="destructive" onClick={() => handleArchive(item.content_id)}>
                        <Archive className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {content.length === 0 && (
          <div className="text-center py-12 text-slate-400">
            <Image className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No content found</p>
            <p className="text-sm">Upload images, videos, or create widgets</p>
          </div>
        )}
      </main>

      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="bg-slate-800 text-white border-slate-700 max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add New Content</DialogTitle>
            <DialogDescription>Upload media or create a widget</DialogDescription>
          </DialogHeader>
          
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid grid-cols-2 w-full">
              <TabsTrigger value="upload">Upload Media</TabsTrigger>
              <TabsTrigger value="widget">Create Widget</TabsTrigger>
            </TabsList>
            
            <TabsContent value="upload" className="space-y-4 mt-4">
              <div>
                <Label>Content Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Welcome Banner"
                  className="bg-slate-700 border-slate-600"
                />
              </div>
              
              <div>
                <Label>Content Type *</Label>
                <Select value={formData.content_type} onValueChange={(v) => setFormData({ ...formData, content_type: v })}>
                  <SelectTrigger className="bg-slate-700 border-slate-600">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="image">Image</SelectItem>
                    <SelectItem value="video">Video</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>File *</Label>
                <div className="mt-2">
                  <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-slate-600 border-dashed rounded-lg cursor-pointer bg-slate-700 hover:bg-slate-600">
                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                      <Upload className="h-8 w-8 text-slate-400 mb-2" />
                      <p className="text-sm text-slate-400">
                        {uploadFile ? uploadFile.name : "Click to upload or drag and drop"}
                      </p>
                      <p className="text-xs text-slate-500 mt-1">
                        {formData.content_type === "image" ? "PNG, JPG, GIF, WebP" : "MP4, WebM, MOV"}
                      </p>
                    </div>
                    <input
                      type="file"
                      className="hidden"
                      accept={formData.content_type === "image" ? "image/*" : "video/*"}
                      onChange={(e) => setUploadFile(e.target.files[0])}
                    />
                  </label>
                </div>
              </div>
              
              <div>
                <Label>Duration (seconds)</Label>
                <Input
                  type="number"
                  value={formData.default_duration_seconds}
                  onChange={(e) => setFormData({ ...formData, default_duration_seconds: parseInt(e.target.value) || 10 })}
                  className="bg-slate-700 border-slate-600"
                />
              </div>
              
              <Button onClick={handleUpload} disabled={uploading} className="w-full bg-emerald-600 hover:bg-emerald-700">
                {uploading ? "Uploading..." : "Upload Content"}
              </Button>
            </TabsContent>
            
            <TabsContent value="widget" className="space-y-4 mt-4">
              <div>
                <Label>Widget Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Welcome Message"
                  className="bg-slate-700 border-slate-600"
                />
              </div>
              
              <div>
                <Label>Widget Type *</Label>
                <Select value={formData.widget_type} onValueChange={(v) => setFormData({ ...formData, widget_type: v })}>
                  <SelectTrigger className="bg-slate-700 border-slate-600">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {WIDGET_TYPES.map(type => (
                      <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Widget Config */}
              <div className="space-y-4 p-4 bg-slate-700 rounded-lg">
                <h4 className="font-medium">Widget Configuration</h4>
                
                <div>
                  <Label>Title</Label>
                  <Input
                    value={formData.widget_config.title}
                    onChange={(e) => setFormData({
                      ...formData,
                      widget_config: { ...formData.widget_config, title: e.target.value }
                    })}
                    placeholder="Main heading"
                    className="bg-slate-600 border-slate-500"
                  />
                </div>
                
                <div>
                  <Label>Message</Label>
                  <Textarea
                    value={formData.widget_config.message}
                    onChange={(e) => setFormData({
                      ...formData,
                      widget_config: { ...formData.widget_config, message: e.target.value }
                    })}
                    placeholder="Your message here..."
                    className="bg-slate-600 border-slate-500"
                  />
                </div>
                
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Background</Label>
                    <Input
                      type="color"
                      value={formData.widget_config.background_color}
                      onChange={(e) => setFormData({
                        ...formData,
                        widget_config: { ...formData.widget_config, background_color: e.target.value }
                      })}
                      className="h-10 p-1"
                    />
                  </div>
                  <div>
                    <Label>Text Color</Label>
                    <Input
                      type="color"
                      value={formData.widget_config.text_color}
                      onChange={(e) => setFormData({
                        ...formData,
                        widget_config: { ...formData.widget_config, text_color: e.target.value }
                      })}
                      className="h-10 p-1"
                    />
                  </div>
                  <div>
                    <Label>Font Size</Label>
                    <Select
                      value={formData.widget_config.font_size}
                      onValueChange={(v) => setFormData({
                        ...formData,
                        widget_config: { ...formData.widget_config, font_size: v }
                      })}
                    >
                      <SelectTrigger className="bg-slate-600 border-slate-500">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {FONT_SIZES.map(size => (
                          <SelectItem key={size.value} value={size.value}>{size.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                {/* Live Preview */}
                <div>
                  <Label>Preview</Label>
                  <div className="mt-2 rounded overflow-hidden">
                    <WidgetPreview config={formData.widget_config} widgetType={formData.widget_type} />
                  </div>
                </div>
              </div>
              
              <div>
                <Label>Duration (seconds)</Label>
                <Input
                  type="number"
                  value={formData.default_duration_seconds}
                  onChange={(e) => setFormData({ ...formData, default_duration_seconds: parseInt(e.target.value) || 10 })}
                  className="bg-slate-700 border-slate-600"
                />
              </div>
              
              <Button onClick={handleCreateWidget} className="w-full bg-emerald-600 hover:bg-emerald-700">
                Create Widget
              </Button>
            </TabsContent>
          </Tabs>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCreateDialog(false); resetForm(); setUploadFile(null); }}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="bg-slate-800 text-white border-slate-700 max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Content</DialogTitle>
            <DialogDescription>Update content settings</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Name</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="bg-slate-700 border-slate-600"
              />
            </div>
            
            <div>
              <Label>Status</Label>
              <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                <SelectTrigger className="bg-slate-700 border-slate-600">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CONTENT_STATUS.map(status => (
                    <SelectItem key={status.value} value={status.value}>{status.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Duration (seconds)</Label>
              <Input
                type="number"
                value={formData.default_duration_seconds}
                onChange={(e) => setFormData({ ...formData, default_duration_seconds: parseInt(e.target.value) || 10 })}
                className="bg-slate-700 border-slate-600"
              />
            </div>
            
            {editingContent?.content_type === "widget" && (
              <div className="space-y-4 p-4 bg-slate-700 rounded-lg">
                <h4 className="font-medium">Widget Configuration</h4>
                
                <div>
                  <Label>Title</Label>
                  <Input
                    value={formData.widget_config.title}
                    onChange={(e) => setFormData({
                      ...formData,
                      widget_config: { ...formData.widget_config, title: e.target.value }
                    })}
                    className="bg-slate-600 border-slate-500"
                  />
                </div>
                
                <div>
                  <Label>Message</Label>
                  <Textarea
                    value={formData.widget_config.message}
                    onChange={(e) => setFormData({
                      ...formData,
                      widget_config: { ...formData.widget_config, message: e.target.value }
                    })}
                    className="bg-slate-600 border-slate-500"
                  />
                </div>
                
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Background</Label>
                    <Input
                      type="color"
                      value={formData.widget_config.background_color}
                      onChange={(e) => setFormData({
                        ...formData,
                        widget_config: { ...formData.widget_config, background_color: e.target.value }
                      })}
                      className="h-10 p-1"
                    />
                  </div>
                  <div>
                    <Label>Text Color</Label>
                    <Input
                      type="color"
                      value={formData.widget_config.text_color}
                      onChange={(e) => setFormData({
                        ...formData,
                        widget_config: { ...formData.widget_config, text_color: e.target.value }
                      })}
                      className="h-10 p-1"
                    />
                  </div>
                  <div>
                    <Label>Font Size</Label>
                    <Select
                      value={formData.widget_config.font_size}
                      onValueChange={(v) => setFormData({
                        ...formData,
                        widget_config: { ...formData.widget_config, font_size: v }
                      })}
                    >
                      <SelectTrigger className="bg-slate-600 border-slate-500">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {FONT_SIZES.map(size => (
                          <SelectItem key={size.value} value={size.value}>{size.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div>
                  <Label>Preview</Label>
                  <div className="mt-2 rounded overflow-hidden">
                    <WidgetPreview config={formData.widget_config} widgetType={editingContent?.widget_type} />
                  </div>
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowEditDialog(false); setEditingContent(null); resetForm(); }}>
              Cancel
            </Button>
            <Button onClick={handleUpdate} className="bg-emerald-600 hover:bg-emerald-700">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={showPreviewDialog} onOpenChange={setShowPreviewDialog}>
        <DialogContent className="bg-slate-800 text-white border-slate-700 max-w-4xl">
          <DialogHeader>
            <DialogTitle>Content Preview: {previewContent?.name}</DialogTitle>
          </DialogHeader>
          
          <div className="aspect-video bg-black rounded overflow-hidden flex items-center justify-center">
            {previewContent?.content_type === "image" && previewContent?.media_url && (
              <img src={`${API_URL}${previewContent.media_url}`} alt={previewContent.name} className="max-w-full max-h-full object-contain" />
            )}
            {previewContent?.content_type === "video" && previewContent?.media_url && (
              <video src={`${API_URL}${previewContent.media_url}`} controls className="max-w-full max-h-full" />
            )}
            {previewContent?.content_type === "widget" && (
              <div className="w-full h-full flex items-center justify-center p-8">
                <WidgetPreview config={previewContent.widget_config} widgetType={previewContent.widget_type} />
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreviewDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
